import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, LogOut, Loader2, CheckCircle2, Inbox } from "lucide-react";
import { AdminChatInbox } from "@/components/AdminChatInbox";
import { AdminReportUpload } from "@/components/AdminReportUpload";
import { AdminReviewNudge } from "@/components/AdminReviewNudge";
import { AdminDigest } from "@/components/AdminDigest";
import { CustomerReports } from "@/components/CustomerReports";
import { CustomerAssistant } from "@/components/CustomerAssistant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(location.state?.user ? true : null);
  const [payments, setPayments] = useState([]);
  const [contacts, setContacts] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (location.state?.user) return;
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
        setAuthState(true);
      } catch {
        setAuthState(false);
        navigate("/login");
      }
    };
    checkAuth();
  }, [location.state, navigate]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/me/payments`, { withCredentials: true })
      .then((res) => setPayments(res.data.payments))
      .catch(() => {});
    if (user.role === "admin") {
      axios.get(`${API}/admin/contacts`, { withCredentials: true })
        .then((res) => setContacts(res.data.contacts))
        .catch(() => setContacts([]));
    }
  }, [user]);

  const [portalLoading, setPortalLoading] = useState(false);

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/me/portal`, {}, { withCredentials: true });
      window.location.href = res.data.portal_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open subscription management.");
      setPortalLoading(false);
    }
  };

  const handleSubscribe = async (packageId) => {
    setCheckoutLoading(packageId);
    try {
      const res = await axios.post(
        `${API}/payments/checkout`,
        { package_id: packageId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setCheckoutLoading(false);
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    navigate("/");
  };

  if (authState !== true || !user) {
    return (
      <div className="min-h-screen bg-[#060D1A] flex items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const activeSub = payments.find((p) => p.payment_status === "paid");

  return (
    <div className="min-h-screen bg-[#060D1A] text-slate-100" data-testid="dashboard-page">
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-bold text-lg text-white" data-testid="dashboard-brand">
            OG<span className="text-slate-500 font-mono-label text-xs ml-2 tracking-[0.3em]">SOLUTIONS</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/portal"
              data-testid="dashboard-portal-link"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-300 transition-colors duration-300 hover:bg-white hover:text-[#060D1A]"
            >
              Progress Portal
            </Link>
            <button
              data-testid="dashboard-logout-button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-300 transition-colors duration-300 hover:bg-white hover:text-[#060D1A]"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 space-y-8">
        <div className="rounded-3xl border border-white/15 bg-[#0F1B31]/80 p-8 flex items-center gap-5" data-testid="dashboard-profile-card">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-14 w-14 rounded-full border border-white/20" referrerPolicy="no-referrer" />
          ) : (
            <span className="h-14 w-14 rounded-full bg-white text-[#060D1A] flex items-center justify-center font-display font-bold text-xl">
              {user.name?.[0] || "?"}
            </span>
          )}
          <div>
            <p className="font-display text-xl font-semibold text-white" data-testid="dashboard-user-name">{user.name}</p>
            <p className="text-sm text-slate-400" data-testid="dashboard-user-email">{user.email}</p>
          </div>
          {user.role === "admin" && (
            <span className="ml-auto font-mono-label text-[10px] uppercase tracking-[0.25em] rounded-full border border-white/25 px-3 py-1.5 text-white" data-testid="dashboard-admin-badge">
              Admin
            </span>
          )}
        </div>

        <div className="rounded-3xl border border-white/15 bg-[#0F1B31]/80 p-8" data-testid="dashboard-subscription-card">
          <p className="font-mono-label text-xs uppercase tracking-[0.3em] text-slate-400 mb-4">Subscription</p>
          {activeSub ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-testid="dashboard-subscription-active">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-white" />
                <div>
                  <p className="font-display text-xl font-semibold text-white">Subscription — Active</p>
                  <p className="text-sm text-slate-400">Renews automatically — manage it anytime</p>
                </div>
              </div>
              <button
                data-testid="manage-subscription-button"
                onClick={handlePortal}
                disabled={portalLoading}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors duration-300 hover:bg-white hover:text-[#060D1A] disabled:opacity-50"
              >
                {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                Manage or Cancel
              </button>
            </div>
          ) : (
            <div data-testid="dashboard-plan-picker">
              <p className="font-display text-xl font-semibold text-white">Choose Your Plan</p>
              <p className="text-sm text-slate-400 mt-1">Every plan includes a website we build and manage for you.</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { id: "og_monthly", name: "Monthly", price: "$199 CAD/mo", note: "Website + management + audits" },
                  { id: "og_annual", name: "Annual", price: "$2,000 CAD/yr", note: "Same as monthly — 2 months free" },
                  { id: "og_website", name: "Website / App", price: "$500 + $50/mo", note: "Just the build, managed" },
                ].map((p) => (
                  <button
                    key={p.id}
                    data-testid={`dashboard-subscribe-${p.id}`}
                    onClick={() => handleSubscribe(p.id)}
                    disabled={checkoutLoading !== false}
                    className="rounded-2xl border border-white/15 p-5 text-left transition-colors duration-300 hover:border-white/40 hover:bg-white/[0.04] disabled:opacity-60"
                  >
                    <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-slate-500">{p.name}</p>
                    <p className="mt-2 font-display text-lg font-bold text-white flex items-center gap-2">
                      {checkoutLoading === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                      {p.price}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">{p.note}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <CustomerReports />

        <CustomerAssistant />

        {user.role === "admin" && (
          <div className="rounded-3xl border border-white/15 bg-[#0F1B31]/80 p-8" data-testid="admin-contacts-section">
            <p className="font-mono-label text-xs uppercase tracking-[0.3em] text-slate-400 mb-6 flex items-center gap-2">
              <Inbox className="h-4 w-4" /> Free Call Requests
            </p>
            {contacts === null ? (
              <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            ) : contacts.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="admin-contacts-empty">No requests yet.</p>
            ) : (
              <div className="overflow-x-auto" data-testid="admin-contacts-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left font-mono-label text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/10">
                      <th className="pb-3 pr-4">Date</th>
                      <th className="pb-3 pr-4">Name</th>
                      <th className="pb-3 pr-4">Business</th>
                      <th className="pb-3 pr-4">Phone</th>
                      <th className="pb-3">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c) => (
                      <tr key={c.id} className="border-b border-white/5 text-slate-300" data-testid={`admin-contact-row-${c.id}`}>
                        <td className="py-3 pr-4 whitespace-nowrap text-slate-500">{new Date(c.created_at).toLocaleDateString()}</td>
                        <td className="py-3 pr-4">{c.name}</td>
                        <td className="py-3 pr-4">{c.business_name}</td>
                        <td className="py-3 pr-4">{c.phone}</td>
                        <td className="py-3">{c.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {user.role === "admin" && (
          <>
            <AdminReportUpload />
            <AdminReviewNudge />
            <AdminChatInbox />
            <AdminDigest />
          </>
        )}
      </main>
    </div>
  );
}
